import { useEffect, useMemo, useState } from 'react';
import { CalendarDate } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { IEmployerCalendarEventDtoCategoryEnum } from '@/dtos';

import { EmployerCalendarPayload } from '../EmployerCalendar.types';
import { useGetEmployerEventsQuery } from '../queries';

interface Params {
  currentType?: IEmployerCalendarEventDtoCategoryEnum | null;
}

const useEmployerCalendarEvents = ({ currentType }: Params) => {
  const [selectedDate, setSelectedDate] = useState<CalendarDate | null | undefined>(null);

  const [currentMonth, setCurrentMonth] = useState(dayjs());
  useEffect(() => {
    if (selectedDate) {
      setSelectedDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);
  const startDate = useMemo(() => {
    return currentMonth.startOf('month').format(RAW_DATE_FORMAT);
  }, [currentMonth]);
  const endDate = useMemo(() => {
    return currentMonth.endOf('month').format(RAW_DATE_FORMAT);
  }, [currentMonth]);
  const { dataMap, isLoading, isError } = useGetEmployerEventsQuery({
    currentType,
    startDate: startDate,
    endDate: endDate,
  });

  const handleResetSelectedDate = () => {
    setSelectedDate(null);
  };

  const currentEvents = useMemo(() => {
    if (selectedDate) {
      const key = selectedDate.format(DEFAULT_DATE_FORMAT);
      return dataMap[key] || [];
    }
    const startDate = currentMonth.startOf('month');
    const endDate = currentMonth.endOf('month');

    const totalDays = endDate.diff(startDate, 'days') + 1;

    const keysArray = Array.from({ length: totalDays }, (_, i) =>
      startDate.add(i, 'day').format(DEFAULT_DATE_FORMAT)
    );
    const list = keysArray.reduce((result: EmployerCalendarPayload[], item) => {
      if (dataMap[item]) {
        return [...result, ...dataMap[item]];
      }
      return result;
    }, []);
    return list.reverse();
  }, [currentMonth, dataMap, selectedDate]);
  const isEmpty = useMemo(() => {
    if (selectedDate && !currentEvents.length) {
      return true;
    }
    return _.isEmpty(dataMap);
  }, [currentEvents.length, dataMap, selectedDate]);
  return {
    isError,
    isLoading,
    currentEvents,
    isEmpty,
    handleResetSelectedDate,
    selectedDate,
    setSelectedDate,
    dataMap,
    currentMonth,
    setCurrentMonth,
  };
};
export default useEmployerCalendarEvents;
